// (Truncated import list for brevity – keep as in original)
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
  Button,
  IconButton,
  Divider,
  FormHelperText,
  useTheme,
  Snackbar,
  Alert,
  Link,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  AlertTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import DeleteIcon from "@mui/icons-material/Delete";
import { usePreferences } from "../contexts/PreferencesContext";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { encryptField } from "../utils/encryption";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  Timestamp,
  query,
  where,
  getDocs,
  onSnapshot,
  updateDoc,
  deleteDoc,
  orderBy,
  limit,
  getCountFromServer,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { PDFViewer } from "@react-pdf/renderer";
import InvoicePDF from "../components/InvoicePDF";
import { useNavigate } from "react-router-dom";
import ClientSelect from "../components/clients/ClientSelect";
import { decryptField } from "../utils/encryption";
import api from "../services/api";
import { useTrial } from "../contexts/TrialContext";
import UpgradePrompt from "../components/UpgradePrompt";

export default function InvoiceForm() {
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    companyName,
    companyAddress,
    companyNumber,
    accountantEmail,
    logoUrl,
    bankDetails,
    setBankDetails,
    refreshPreferences,
    preferencesLoaded,
  } = usePreferences();

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [dueDate, setDueDate] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientDetails, setClientDetails] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [logoBase64, setLogoBase64] = useState("");
  const [vatNote, setVatNote] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([
    { description: "", quantity: "", unitPrice: "", vat: "" },
  ]);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [currency, setCurrency] = useState("EUR");

  const getCurrencySymbol = (code) => {
    switch (code) {
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      case "USD":
        return "$";
      default:
        return "";
    }
  };

  const formatAmount = (num) =>
    new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);

  const parseNumber = (val) => (isNaN(parseFloat(val)) ? 0 : parseFloat(val));
  const calculateLineNet = (item) =>
    parseNumber(item.quantity) * parseNumber(item.unitPrice);
  const calculateLineVAT = (item) =>
    (parseNumber(item.vat) / 100) * calculateLineNet(item);
  const calculateLineTotal = (item) =>
    calculateLineNet(item) + calculateLineVAT(item);

  const subtotal = items.reduce((sum, item) => sum + calculateLineNet(item), 0);
  const vatTotal = items.reduce((sum, item) => sum + calculateLineVAT(item), 0);
  const totalAmount = subtotal + vatTotal;

  const invoiceData = useMemo(() => {
    if (!preferencesLoaded) return null;
    return {
      bankDetails: bankDetails,
      invoiceNumber,
      invoiceDate,
      dueDate,
      clientId,
      clientName: clientDetails?.name || "",
      clientCompany: clientDetails?.company || "",
      clientAddress: clientDetails?.address || "",
      clientEmail: clientDetails?.email || "",
      currency,
      vatNote,
      notes,
      items,
      subtotal,
      vatTotal,
      totalAmount,
      logoUrl,
      logoBase64,
      companyName,
      companyAddress: companyAddress,
      companyNumber: companyNumber,
    };
  }, [
    preferencesLoaded,
    bankDetails,
    invoiceNumber,
    invoiceDate,
    dueDate,
    clientId,
    clientDetails,
    currency,
    vatNote,
    notes,
    items,
    subtotal,
    vatTotal,
    totalAmount,
    logoUrl,
    logoBase64,
    companyName,
    companyAddress,
    companyNumber,
  ]);

  useEffect(() => {
    refreshPreferences();
  }, []);

  // useEffect(() => {
  //   const loadLogo = async () => {
  //     if (!logoUrl) return;
  //     if (logoBase64) return; // already loaded — don't refetch
  //     const base64 = await fetchLogoBase64(logoUrl);
  //     if (base64) {
  //       setLogoBase64(`data:image/png;base64,${base64}`);
  //     }
  //   };
  //   loadLogo();
  // }, [logoUrl, logoBase64]); // Prevent unnecessary re-run

  useEffect(() => {
    const fetchProjects = async () => {
      if (!auth.currentUser) return;
      const q = query(
        collection(db, "projects"),
        where("uid", "==", auth.currentUser.uid),
      );
      const snap = await getDocs(q);
      setProjects(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchClientDetails = async () => {
      if (!clientId) return;
      const ref = doc(db, "users", auth.currentUser.uid, "clients", clientId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;

      const data = snap.data();
      setClientDetails({
        name: decryptField(data.name),
        email: decryptField(data.email),
        company: decryptField(data.company),
        address: decryptField(data.address),
        notes: decryptField(data.notes),
      });
    };
    fetchClientDetails();
  }, [clientId]);

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    setItems(updatedItems);
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      { description: "", quantity: "", unitPrice: "", vat: "" },
    ]);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const validate = () => {
    const newErrors = {};
    if (!invoiceNumber) newErrors.invoiceNumber = "Required";
    if (!invoiceDate) newErrors.invoiceDate = "Required";
    if (!clientId) newErrors.clientId = "Required";

    if (items.length === 0 || !items.some((i) => i.description)) {
      newErrors.items = "Add at least one valid item";
    } else {
      items.forEach((item, idx) => {
        if (!item.description.trim()) newErrors[`item-desc-${idx}`] = true;
        if (!item.quantity || parseFloat(item.quantity) <= 0)
          newErrors[`item-qty-${idx}`] = true;
        if (!item.unitPrice || parseFloat(item.unitPrice) < 0)
          newErrors[`item-price-${idx}`] = true;
      });
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      const firstErrorKey = Object.keys(validationErrors)[0];
      setTimeout(() => {
        const el = document.querySelector(`[name="${firstErrorKey}"]`);
        if (el) el.focus();
      }, 0);
      setSnackbar({
        open: true,
        message: "Please fill all required fields.",
        severity: "error",
      });
      return;
    }

    const userId = auth.currentUser?.uid;
    if (!userId) return alert("Please log in.");
    const now = new Date();

    // Use invoice date for month/year, not creation date
    const invDate = invoiceDate ? new Date(invoiceDate) : now;
    const invoiceMonth = invDate.getMonth() + 1; // 1-indexed for storage
    const invoiceYear = invDate.getFullYear();

    const { logoBase64, ...invoiceDataToSave } = invoiceData;

    const encryptedInvoice = {
      ...invoiceDataToSave,
      ...(invoiceDataToSave.logoUrl
        ? { logoUrl: invoiceDataToSave.logoUrl }
        : {}),
      clientEmail: encryptField(invoiceDataToSave.clientEmail),
      clientCompany: encryptField(invoiceDataToSave.clientCompany),
      clientAddress: encryptField(invoiceDataToSave.clientAddress),
      clientName: encryptField(invoiceDataToSave.clientName),
      bankDetails: encryptField(invoiceDataToSave.bankDetails),
      companyAddress: encryptField(invoiceDataToSave.companyAddress),
      companyNumber: encryptField(invoiceDataToSave.companyNumber),
      notes: encryptField(invoiceDataToSave.notes),
    };

    const removeUndefinedFields = (obj) =>
      Object.fromEntries(
        Object.entries(obj).filter(([_, v]) => v !== undefined),
      );

    // Convert to EUR if invoice is in foreign currency
    let eurFields = {};
    if (currency !== "EUR") {
      try {
        const response = await api.post("/invoices/convert-to-eur", {
          amount: totalAmount,
          currency: currency,
          date: invoiceDate,
        });
        const { exchangeRate } = response.data;
        eurFields = {
          exchangeRate,
          exchangeRateDate: invoiceDate,
          eurSubtotal: parseFloat((subtotal / exchangeRate).toFixed(2)),
          eurVatTotal: parseFloat((vatTotal / exchangeRate).toFixed(2)),
          eurTotalAmount: parseFloat((totalAmount / exchangeRate).toFixed(2)),
        };
      } catch (err) {
        console.error("Failed to fetch exchange rate:", err);
        // Continue without EUR conversion - will be handled by migration
      }
    }

    await addDoc(
      collection(db, "invoices"),
      removeUndefinedFields({
        ...encryptedInvoice,
        projectId,
        currency,
        userId,
        invoiceMonth,
        invoiceYear,
        status: "unpaid",
        subtotal: parseFloat(subtotal.toFixed(2)),
        vatTotal: parseFloat(vatTotal.toFixed(2)),
        totalAmount: parseFloat(totalAmount.toFixed(2)),
        ...eurFields, // Include EUR conversion if applicable
        createdAt: Timestamp.fromDate(now),
        type: "invoice",
      }),
    );

    setSnackbar({
      open: true,
      message: "Invoice created successfully!",
      severity: "success",
    });
    setTimeout(() => navigate("/dashboard/invoices"), 1500);
  };

  const { plan, isTrialExpired } = useTrial();
  const isPro = plan === "pro" || plan === "proplus";

  if (!preferencesLoaded) return null;

  if (!isPro && isTrialExpired) {
    return (
      <Box maxWidth="800px" mx="auto" mt={8} px={3}>
        <UpgradePrompt
          source="invoice-form"
          title="🚫 Invoice creation is now disabled."
          subtitle="Your 14-day trial has ended. Upgrade to Pro to continue creating and sending invoices."
        />
      </Box>
    );
  }

  return (
    <Box maxWidth="1000px" mx="auto" p={3}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Create Invoice
      </Typography>
      {companyName && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Invoices will be sent from <strong>{companyName}</strong>
          {companyNumber && ` (Company No: ${companyNumber})`}
          {companyAddress && `, ${companyAddress}`}.{" "}
          <Link href="/dashboard/settings" underline="hover">
            Edit in Settings
          </Link>
        </Alert>
      )}

      {/* Client Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Client Information
        </Typography>
        <ClientSelect value={clientId} onChange={setClientId} />
        {errors.clientId && (
          <FormHelperText error sx={{ mt: -1, mb: 1 }}>
            Client is required
          </FormHelperText>
        )}
        {clientDetails && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              <strong>Company:</strong> {clientDetails.company}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Email:</strong> {clientDetails.email}
            </Typography>
            {clientDetails.address && (
              <Typography variant="body2" color="textSecondary">
                <strong>Address:</strong> {clientDetails.address}
              </Typography>
            )}
            {clientDetails.notes && (
              <Typography variant="body2" color="textSecondary">
                <strong>Notes:</strong> {clientDetails.notes}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Invoice Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Invoice Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="project-select-label">Project</InputLabel>
              <Select
                labelId="project-select-label"
                value={projectId}
                label="Project"
                onChange={(e) => setProjectId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {projects.map((proj) => (
                  <MenuItem key={proj.id} value={proj.id}>
                    {proj.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Invoice Number"
              fullWidth
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              error={!!errors.invoiceNumber}
              helperText={errors.invoiceNumber}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Invoice Date"
                value={invoiceDate ? new Date(invoiceDate) : null}
                onChange={(newValue) => {
                  if (newValue)
                    setInvoiceDate(newValue.toISOString().slice(0, 10));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.invoiceDate,
                    helperText: errors.invoiceDate,
                    sx: { cursor: "pointer" },
                  },
                }}
                format="dd/MM/yyyy"
                disableFuture={false}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              select
              label="Currency"
              fullWidth
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="EUR">EUR ({getCurrencySymbol(currency)})</option>
              <option value="GBP">GBP (£)</option>
              <option value="USD">USD ($)</option>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Line Items */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Line Items
        </Typography>
        {items.map((item, idx) => (
          <Grid
            container
            spacing={1}
            key={idx}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Grid size={3}>
              <TextField
                name={`item-desc-${idx}`}
                fullWidth
                label="Description"
                value={item.description}
                onChange={(e) =>
                  handleItemChange(idx, "description", e.target.value)
                }
                error={!!errors[`item-desc-${idx}`]}
                helperText={errors[`item-desc-${idx}`] ? "Required" : ""}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                name={`item-qty-${idx}`}
                type="number"
                label="Qty"
                fullWidth
                value={item.quantity}
                onChange={(e) =>
                  handleItemChange(idx, "quantity", e.target.value)
                }
                error={!!errors[`item-qty-${idx}`]}
                helperText={errors[`item-qty-${idx}`] ? "Required" : ""}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                name={`item-price-${idx}`}
                type="number"
                label="Unit Price"
                fullWidth
                value={item.unitPrice}
                onChange={(e) =>
                  handleItemChange(idx, "unitPrice", e.target.value)
                }
                error={!!errors[`item-price-${idx}`]}
                helperText={errors[`item-price-${idx}`] ? "Required" : ""}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                type="number"
                label="VAT %"
                fullWidth
                value={item.vat}
                onChange={(e) => handleItemChange(idx, "vat", e.target.value)}
                error={parseNumber(item.vat) > 100}
                helperText={parseNumber(item.vat) > 100 ? "Max 100%" : ""}
              />
            </Grid>
            <Grid size={3}>
              <Typography variant="body2" color="text.secondary">
                Net: {getCurrencySymbol(currency)}
                {formatAmount(calculateLineNet(item))} | VAT:{" "}
                {getCurrencySymbol(currency)}
                {formatAmount(calculateLineVAT(item))}
                <br />
                <strong>
                  Total: {getCurrencySymbol(currency)}
                  {formatAmount(calculateLineTotal(item))}
                </strong>
              </Typography>
            </Grid>
            <Grid size={1} display="flex" justifyContent="center">
              <IconButton onClick={() => handleRemoveItem(idx)} color="error">
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button startIcon={<AddIcon />} onClick={handleAddItem} sx={{ mt: 1 }}>
          Add Item
        </Button>
        {errors.items && (
          <FormHelperText error sx={{ mt: 1 }}>
            {errors.items}
          </FormHelperText>
        )}
      </Paper>

      {/* Notes */}
      <Paper
        sx={{
          p: 3,
          position: "sticky",
          top: 80,
          width: "100%",
          // maxWidth: 340,
        }}
      >
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Notes
        </Typography>
        <TextareaAutosize
          minRows={3}
          placeholder="Enter notes (e.g., payment terms, IBAN)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            fontSize: "1rem",
            fontFamily: "inherit",
          }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          Bank Details
        </Typography>
        <TextareaAutosize
          minRows={3}
          placeholder="Enter bank details for footer (e.g., IBAN, BIC)"
          value={bankDetails}
          onChange={(e) => setBankDetails(e.target.value)}
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            fontSize: "1rem",
            fontFamily: "inherit",
          }}
        />
      </Paper>

      {/* Summary */}
      <Paper
        sx={{
          p: 3,
          position: "sticky",
          top: 80,
          width: "100%",
          // maxWidth: 340,
        }}
      >
        <Typography variant="subtitle1" gutterBottom>
          Summary
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography>
          Subtotal: {getCurrencySymbol(currency)}
          {formatAmount(subtotal)}
        </Typography>
        <Typography>
          VAT Total: {getCurrencySymbol(currency)}
          {formatAmount(vatTotal)}
        </Typography>
        <Typography fontWeight={600} mt={1}>
          Total: {getCurrencySymbol(currency)}
          {formatAmount(totalAmount)}
        </Typography>

        {logoUrl && (
          <Box display="flex" alignItems="center" mb={1} mt={2}>
            <Box
              component="img"
              src={logoUrl}
              alt="Logo"
              sx={{ height: 32, mr: 1, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Your logo will appear on the invoice
            </Typography>
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ mt: 3, px: 4 }}
            disabled={
              !invoiceNumber || !invoiceDate || !clientId || items.length === 0
            }
          >
            Create Invoice
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
